import { computed, inject, ref } from 'vue';
// eslint-disable-next-line prettier/prettier
import type { Ref } from 'vue';
import useOrderTypes from '@/composables/useOrderTypes';
import useCartControls from '@/composables/useCartControls';
import useQuantityDisplay from '@/modules/orders/composables/useQuantityDisplay';
import modalService from '@/modules/modals/services/modal.service';
import WCOrderTypeCartPicker from '@/modules/orderTypes/components/WCOrderTypeCartPicker/WCOrderTypeCartPicker.vue';
import { configurationKey } from '@/injections';
import ItemSummary from '@/models/entities/items/item-summary';
import OrderType from '@/models/entities/cart/order-type';

export interface OrderTypeWithQuantity {
  orderType: OrderType;
  quantity: number;
}

/**
 * What the card should show, decided purely from real quantities:
 * - `splitBase` — nothing added anywhere yet, current cart is one of the eligible ones: Add + ⋮
 * - `stepper` — current cart has real quantity
 * - `inOtherCart` / `inOtherCarts` — nothing in the current cart, but 1 (or 2+) other carts do
 * - `plainBase` — nothing added anywhere yet, current cart isn't even eligible for this item
 */
export type MultiOTCardState =
  | 'splitBase'
  | 'stepper'
  | 'inOtherCart'
  | 'inOtherCarts'
  | 'plainBase';

export default function useMultiOTPicker(item: Ref<ItemSummary>) {
  const configuration = inject(configurationKey);
  const { eligibleOrderTypesForItem, isAcoMode, activeOrderType } = useOrderTypes();
  const { isSoldByRandomWeight } = useQuantityDisplay();

  const eligibleOrderTypes = computed<OrderType[]>(() =>
    eligibleOrderTypesForItem(item.value).filter((orderType: OrderType) => !orderType.listOnly),
  );

  const hasMultiOTPicker = computed<boolean>(
    () =>
      Boolean(configuration?.orderTypesEnabled) &&
      !isAcoMode.value &&
      !isSoldByRandomWeight(item.value) &&
      eligibleOrderTypes.value.length >= 2,
  );

  // Real quantities, one per eligible order type — read once per order type at setup time (not
  // inside a computed) since getQuantityInCart needs its own injected context to be built. This is
  // the only new fact-finding this feature needs: everything the card shows is a direct readout of
  // these real numbers, nothing is tracked or decided separately.
  //
  // Only done for items that actually get the multi-OT treatment — most items on any catalog page
  // belong to a single order type, and there's no reason for every one of them to pay the cost of
  // an extra cart lookup per order type just to find out they don't need it.
  const quantitiesByOrderType: OrderTypeWithQuantity[] = hasMultiOTPicker.value
    ? eligibleOrderTypes.value.map(orderType => {
        const { getQuantityInCart } = useCartControls(ref(orderType));
        return {
          orderType,
          get quantity() {
            return getQuantityInCart(item.value, null).toNumber();
          },
        };
      })
    : [];

  const currentOrderType = computed<OrderType | undefined>(() =>
    eligibleOrderTypes.value.find(orderType => orderType.id === activeOrderType.value?.id),
  );

  const requiresOtherOTConfirmation = computed<boolean>(
    () =>
      Boolean(configuration?.orderTypesEnabled) &&
      !isAcoMode.value &&
      !isSoldByRandomWeight(item.value) &&
      eligibleOrderTypes.value.length === 1 &&
      !currentOrderType.value,
  );

  const currentQuantity = computed<number>(() => {
    const match = quantitiesByOrderType.find(
      entry => entry.orderType.id === currentOrderType.value?.id,
    );
    return match?.quantity ?? 0;
  });

  const otherOrderTypesWithQuantity = computed<OrderTypeWithQuantity[]>(() =>
    quantitiesByOrderType
      .filter(entry => entry.orderType.id !== currentOrderType.value?.id && entry.quantity > 0)
      .map(entry => ({ orderType: entry.orderType, quantity: entry.quantity })),
  );

  const cardState = computed<MultiOTCardState>(() => {
    if (currentQuantity.value > 0) {
      return 'stepper';
    }
    if (otherOrderTypesWithQuantity.value.length === 1) {
      return 'inOtherCart';
    }
    if (otherOrderTypesWithQuantity.value.length >= 2) {
      return 'inOtherCarts';
    }
    return currentOrderType.value ? 'splitBase' : 'plainBase';
  });

  const openPicker = (variation?: string | null): void => {
    modalService
      .open(WCOrderTypeCartPicker, {
        item: item.value,
        orderTypes: eligibleOrderTypes.value,
        variation,
      })
      .catch(() => {});
  };

  return {
    eligibleOrderTypes,
    hasMultiOTPicker,
    requiresOtherOTConfirmation,
    currentOrderType,
    currentQuantity,
    otherOrderTypesWithQuantity,
    cardState,
    openPicker,
  };
}
